import { Text } from 'components/typography'
import { isValid, parse } from 'date-fns'
import React, { useMemo, useState } from 'react'
import ReactLoading from 'react-loading'
import styled from 'styled-components'
import ActiveInventoryApi from '../../../apis/active-inventory'
import SuccessfullyConcludedDealApi from '../../../apis/successfully-concluded-deal'
import DialogV2 from '../../../components/dialog-v2'
import ButtonV2 from '../../../components/button-v2'
import { Input } from '../../../components/input-control'
import Label from '../../../components/label'
import {
  ActiveInventoryConfig,
  SuccessfullyConcludedDealConfig,
} from '../../../schema/contract'
import theme from '../../../styles/theme'

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const SaveButton = styled(ButtonV2)`
  min-width: 220px;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    min-width: 220px;
  }
`

const Error = styled.label`
  font-style: normal;
  font-weight: 700;
  font-size: ${(p) => p.theme.font.size.m};
  line-height: 16px;
  color: red;
`

const Alert = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: ${(p) => p.theme.font.size.m};
  line-height: 20px;
  color: red;
`

const InputArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const NumberInput = styled(Input)`
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }
  box-shadow: none;
  font-size: ${(p) => p.theme.font.size.m};
  border: 1px solid ${(p) => p.theme.BORDER};
`

const ErrorText = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  color: #ea1a1a;
`

const wheelDisabled = (e: any) => {
  e.target.addEventListener(
    'wheel',
    (e: any) => {
      e.preventDefault()
    },
    { passive: false }
  )
}

const ConfigInput = (props: {
  contractId: string
  config: ActiveInventoryConfig | SuccessfullyConcludedDealConfig | undefined
  onClear: () => void
  close: () => void
  onSuccess: () => void
  onSearchNoData: (type: string) => void
}) => {
  const [address, setAddress] = useState<string | undefined>(
    props.config?.address
  )
  const [showAddressError, setShowAddressError] = useState(false)
  const [addressError, setAddressError] = useState(!props.config?.address)

  const [size, setSize] = useState<number | undefined>(props.config?.square)
  const [showSizeError, setShowSizeError] = useState(false)
  const [sizeError, setSizeError] = useState(!props.config?.square)

  const [buildYearMonth, setBuildYearMonth] = useState<string | undefined>(
    props.config?.buildYearMonth
  )
  const [showBuildYearMonthError, setShowBuildYearMonthError] = useState(false)
  const [buildYearMonthError, setBuildYearMonthError] = useState(
    !props.config?.buildYearMonth
  )

  const [isLoading, setIsLoading] = useState(false)

  const hasError = useMemo(() => {
    return sizeError || addressError || buildYearMonthError
  }, [sizeError, addressError, buildYearMonthError])

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setAddress(value)

    if (!value) {
      setShowAddressError(true)
      setAddressError(true)
      return
    }

    setShowAddressError(false)
    setAddressError(false)
  }

  const handleSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)
    setSize(value)

    if (!value) {
      setShowSizeError(true)
      setSizeError(true)
      return
    }

    if (value < 10 || value > 999.99) {
      setShowSizeError(true)
      setSizeError(true)
      return
    }
    setShowSizeError(false)
    setSizeError(false)
  }

  const handleBuildYearMonthChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value
    setBuildYearMonth(value)

    if (!value) {
      setShowBuildYearMonthError(true)
      setBuildYearMonthError(true)
      return
    }

    if (!validateYearMonthFormat(value)) {
      setShowBuildYearMonthError(true)
      setBuildYearMonthError(true)
      return
    }

    setShowBuildYearMonthError(false)
    setBuildYearMonthError(false)
  }

  const validateYearMonthFormat = (value: string) => {
    const parseDate = parse(value, 'yyyy/MM', new Date())
    return isValid(parseDate)
  }

  const onSetup = async () => {
    setIsLoading(true)
    if (address == null || size == null || buildYearMonth == null) {
      return
    }

    try {
      const results = await Promise.all([
        ActiveInventoryApi.setting(
          props.contractId,
          address,
          size,
          buildYearMonth
        ),
        SuccessfullyConcludedDealApi.setting(
          props.contractId,
          address,
          size,
          buildYearMonth
        ),
      ])

      if (results.filter((result) => result.data).length === 0) {
        props.onSearchNoData('notfound')
        return
      }
    } catch (e) {
      setIsLoading(false)
      props.onSearchNoData('error')
      return
    }

    setIsLoading(false)
    props.close()
    props.onSuccess()
  }

  return (
    <>
      <DialogV2.Header
        title="売出物件と成約事例の表示条件"
        showCloseIcon={true}
        titleAlign="left"
        onClose={props.close}
      />
      <DialogV2.Body>
        <Body>
          <InputArea>
            <Label htmlFor="address" labelName="住所" required={true} />
            <Input
              type="text"
              name="address"
              value={address}
              placeholder="例：東京都港区虎ノ門"
              aria-label="住所"
              onChange={handleAddressChange}
            />
            <Text size={'md'}>
              市区もしくは町名まで入力してください。
              <br />
              都内であれば町名まで指定、それ以外では市区町村までの指定が目安です。
            </Text>
            {showAddressError && <ErrorText>住所を入力してください</ErrorText>}
          </InputArea>
          <InputArea>
            <Label
              htmlFor="size"
              labelName="面積（単位：㎡）"
              required={true}
            />
            <NumberInput
              type="number"
              name="size"
              value={size}
              placeholder="例：120.05"
              aria-label="面積（単位：㎡）"
              onChange={handleSizeChange}
              onFocus={wheelDisabled}
              step="0.01"
            />
            <Text size={'md'}>入力値の近似値順に表示されます。</Text>
            {showSizeError && (
              <ErrorText>
                平米数は10.00 - 999.99までの数値で入力してください
              </ErrorText>
            )}
          </InputArea>
          <InputArea>
            <Label
              htmlFor="buildYearMonth"
              labelName="築年月"
              required={true}
            />
            <Input
              type="text"
              name="buildYearMonth"
              value={buildYearMonth}
              placeholder="例：2020/01"
              aria-label="築年月"
              onChange={handleBuildYearMonthChange}
            />
            <Text size={'md'}>前後5年の物件情報が表示されます。</Text>
            {showBuildYearMonthError && (
              <ErrorText>正しい年月を指定してください</ErrorText>
            )}
          </InputArea>
        </Body>
      </DialogV2.Body>
      <DialogV2.Footer layout="spaceBetween">
        <ButtonV2 variant="ghost" onClick={props.onClear} disabled={isLoading}>
          内容をクリア
        </ButtonV2>
        <SaveButton
          variant="primary"
          disabled={!hasError === false || isLoading}
          onClick={onSetup}
          radius="full"
          size="lg"
        >
          {isLoading && (
            <ReactLoading
              type="spin"
              color={theme.WHITE}
              height={20}
              width={20}
            />
          )}
          {!isLoading && '表示条件を保存'}
        </SaveButton>
      </DialogV2.Footer>
    </>
  )
}

const ClearConfirm = (props: {
  onReturn: () => Promise<void>
  onExecute: () => Promise<void>
  onClose: () => void
  isLoading: boolean
}) => {
  return (
    <>
      <DialogV2.Header
        title="設定条件をクリアしますか？"
        showCloseIcon={true}
        titleAlign="left"
        onClose={props.onClose}
        disableClose={props.isLoading}
      />
      <DialogV2.Body>
        <Alert>
          現在設定されている売出物件がすべて解除されます。
          <br />
          解除された物件は、再度設定することで表示できます。
          <br />
          <br />
          このまま操作を続けてもよろしいですか？
        </Alert>
      </DialogV2.Body>
      <DialogV2.Footer layout="spaceBetween">
        <ButtonV2
          variant="ghost"
          onClick={props.onReturn}
          disabled={props.isLoading}
        >
          いいえ
        </ButtonV2>
        <SaveButton
          variant="primary"
          onClick={props.onExecute}
          radius="full"
          disabled={props.isLoading}
        >
          {props.isLoading && (
            <ReactLoading
              type="spin"
              color={theme.WHITE}
              height={20}
              width={20}
            />
          )}
          {!props.isLoading && 'はい'}
        </SaveButton>
      </DialogV2.Footer>
    </>
  )
}

const NoData = (props: {
  onReturn: () => Promise<void>
  onClose: () => void
  messageType?: string
}) => {
  const message =
    props.messageType === 'error' ? (
      <>
        <span>該当住所での周辺物件がありません。</span>
        <br />
        <span>
          市区町村や丁目等の番地情報を含めて再度お試しいただくか、実際に物件がない場合があります。
        </span>
      </>
    ) : (
      <span>該当住所での周辺物件がありません。</span>
    )

  return (
    <>
      <DialogV2.Header
        title="エラー"
        showCloseIcon={true}
        titleAlign="left"
        onClose={props.onClose}
      />
      <DialogV2.Body>
        <Error>{message}</Error>
      </DialogV2.Body>
      <DialogV2.Footer layout="right">
        <ButtonV2 variant="secondary" size="lg" onClick={props.onReturn}>
          戻る
        </ButtonV2>
      </DialogV2.Footer>
    </>
  )
}

const SettingDialog = ({
  contractId,
  config,
  showDialog,
  onCancel,
  onSuccess,
}: {
  contractId: string
  config: ActiveInventoryConfig | SuccessfullyConcludedDealConfig | undefined
  showDialog: boolean
  onCancel: () => void
  onSuccess: () => void
}) => {
  const [isSearchNoDate, setSearchNoDate] = useState(false)
  const [viewConfigInputDialog, setViewConfigInputDialog] = useState(true)
  const [viewClearConfirmDialog, setViewClearConfirmDialog] = useState(false)
  const [messageType, setMessageType] = useState('')

  const [isLoading, setIsLoading] = useState(false)

  const resetDialog = () => {
    setViewConfigInputDialog(true)
    setViewClearConfirmDialog(false)
    setSearchNoDate(false)
    setIsLoading(false)
  }

  const close = async () => {
    onCancel()
    resetDialog()
  }

  const returnToInput = async () => {
    resetDialog()
  }

  const toClearConfirm = async () => {
    setViewConfigInputDialog(false)
    setViewClearConfirmDialog(true)
  }

  const clearSetting = async () => {
    setIsLoading(true)
    await ActiveInventoryApi.clear(contractId)
    await SuccessfullyConcludedDealApi.clear(contractId)
    await close()
    onSuccess()
  }

  const viewNoData = (type: string) => {
    setMessageType(type)
    setSearchNoDate(true)
    setViewConfigInputDialog(false)
  }

  return (
    <>
      {viewConfigInputDialog && (
        <DialogV2
          show={showDialog}
          onClose={close}
          styleOptions={{ position: 'top' }}
        >
          <ConfigInput
            contractId={contractId}
            config={config}
            close={close}
            onClear={toClearConfirm}
            onSuccess={onSuccess}
            onSearchNoData={viewNoData}
          />
        </DialogV2>
      )}
      {viewClearConfirmDialog && (
        <DialogV2
          show={showDialog}
          onClose={close}
          disableClose={isLoading}
          styleOptions={{ position: 'top' }}
        >
          <ClearConfirm
            onReturn={returnToInput}
            onExecute={clearSetting}
            onClose={close}
            isLoading={isLoading}
          />
        </DialogV2>
      )}
      {!viewConfigInputDialog && isSearchNoDate && (
        <DialogV2
          show={showDialog}
          onClose={close}
          styleOptions={{ position: 'top' }}
        >
          <NoData
            onReturn={returnToInput}
            onClose={close}
            messageType={messageType}
          />
        </DialogV2>
      )}
    </>
  )
}

export default SettingDialog
